import { AnyFilesInfo } from "@faro-lotv/data-management";
import {
  createContext,
  PropsWithChildren,
  useContext,
  useMemo,
  useState,
  type JSX,
} from "react";

interface Props {
  /**
   * File collected by the Data Management picker/dropzone for the sheet import.
   * This may be generalized to a file array in the future, but the sheet import only needs a single file.
   */
  sheetFile?: File;
  /**
   * Setter for the file collected by the Data Management picker/dropzone for the sheet import.
   * This may be generalized to a file array in the future, but the sheet import only needs a single file.
   */
  setSheetFile: React.Dispatch<React.SetStateAction<File | undefined>>;
  /**
   * File infos collected by the Data Management picker/dropzone for the scan import.
   */
  scanFileInfos: AnyFilesInfo[];
  /**
   * Setter for the file infos collected by the Data Management picker/dropzone for the scan import.
   */
  setScanFileInfos: React.Dispatch<React.SetStateAction<AnyFilesInfo[]>>;
}

const DataManagementContext = createContext<Props | undefined>(undefined);

/**
 * Hook to use the properties provided by the Data Management context.
 *
 * @returns The properties provided by the Data Management context.
 * @throws Error if the hook is used outside of DataManagementContextProvider.
 */
export function useDataManagementContext(): Props {
  const context = useContext(DataManagementContext);
  if (!context) {
    throw new Error(
      "useDataManagementContext must be used within DataManagementContextProvider",
    );
  }
  return context;
}

/**
 * @param children The child components to be wrapped by the provider.
 * @returns A provider component that provides additional context for the Data Management editor,
 * in particular the file infos collected by the Data Management picker/dropzone.
 */
export function DataManagementContextProvider({
  children,
}: PropsWithChildren): JSX.Element | null {
  const [scanFileInfos, setScanFileInfos] = useState<AnyFilesInfo[]>([]);
  const [sheetFile, setSheetFile] = useState<File>();

  const value = useMemo(
    () => ({
      sheetFile,
      setSheetFile,
      scanFileInfos,
      setScanFileInfos,
    }),
    [scanFileInfos, sheetFile],
  );

  return (
    <DataManagementContext.Provider value={value}>
      {children}
    </DataManagementContext.Provider>
  );
}
